import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight, Clock, User, Sparkles, Lock, CheckCircle, Flower2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const Dashboard = () => {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [specialEnrollments, setSpecialEnrollments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [campusAccess, setCampusAccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [enrollmentsRes, coursesRes, specialRes] = await Promise.all([
          api.get('/enrollments'),
          api.get('/courses'),
          api.get('/my-special-enrollments').catch(() => ({ data: [] }))
        ]);
        setEnrollments(enrollmentsRes.data);
        setCourses(coursesRes.data);
        setSpecialEnrollments(specialRes.data || []);
        // Check campus access
        api.get('/campus/status').then(res => setCampusAccess(res.data.has_access)).catch(() => {});
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 20) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const firstName = user?.name?.split(' ')[0] || 'Estudiante';

  return (
    <div className="pt-20 min-h-screen bg-[hsl(var(--sage-light))]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {/* Welcome Section */}
        <div className="card-soft p-8 mb-12 bg-gradient-to-br from-white to-[hsl(var(--sage-light))]">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <p className="text-[hsl(var(--sage))] font-medium mb-2">{getGreeting()}</p>
              <h1 
                className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] mb-2"
                data-testid="dashboard-welcome"
              >
                Bienvenido/a, {firstName}
              </h1>
              <p className="text-[hsl(var(--text-secondary))]">
                Este es tu espacio de aprendizaje y crecimiento.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center">
                <User className="w-8 h-8 text-[hsl(var(--accent))]" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[hsl(var(--text-secondary))]">Cargando tu espacio...</p>
          </div>
        ) : (
          <>
            {/* My Courses Section */}
            <section className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-heading text-[hsl(var(--text-primary))]">
                  Mis cursos
                </h2>
                {enrollments.length > 0 && (
                  <Link to="/mis-cursos" className="text-[hsl(var(--accent))] hover:underline text-sm">
                    Ver todos
                  </Link>
                )}
              </div>

              {enrollments.length === 0 ? (
                <div className="card-soft text-center p-12">
                  <div className="w-16 h-16 rounded-full bg-[hsl(var(--sage-light))] flex items-center justify-center mx-auto mb-6">
                    <BookOpen className="w-8 h-8 text-[hsl(var(--sage))]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-heading text-[hsl(var(--text-primary))] mb-3">
                    Aún no tienes cursos
                  </h3>
                  <p className="text-[hsl(var(--text-secondary))] mb-6">
                    Explora las formaciones disponibles y comienza tu camino.
                  </p>
                  <Link to="/formaciones" data-testid="dashboard-explore">
                    <Button className="btn-gold">
                      Explorar formaciones
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {enrollments.slice(0, 3).map((enrollment, index) => (
                    <Link 
                      key={enrollment.id}
                      to={`/curso/${enrollment.course_id}`}
                      className="group"
                      data-testid={`enrolled-course-${index}`}
                    >
                      <div className="card-soft overflow-hidden hover-lift">
                        <div className="h-40 rounded-2xl overflow-hidden mb-4">
                          <img
                            src={enrollment.course?.image_url || 'https://images.unsplash.com/photo-1516961642265-531546e84af2?fit=crop&w=800&q=80'}
                            alt={enrollment.course?.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <h3 className="font-heading text-lg text-[hsl(var(--text-primary))] mb-2 group-hover:text-[hsl(var(--accent))] transition-colors">
                          {enrollment.course?.title}
                        </h3>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[hsl(var(--text-tertiary))]">Progreso</span>
                            <span className="text-[hsl(var(--sage))] font-medium">{enrollment.progress}%</span>
                          </div>
                          <Progress value={enrollment.progress} className="h-2" />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            {/* Campus de Interioridad — visible para todos */}
              <section className="mb-12">
                <Link to={campusAccess ? "/campus" : "/campus-interioridad"} className="block group" data-testid="campus-access-card">
                  <div className="relative overflow-hidden rounded-2xl border border-[#c9a227]/30 p-8 hover:border-[#c9a227]/50 transition-all hover:shadow-lg hover:shadow-[#c9a227]/15"
                    style={{ background: 'linear-gradient(135deg, #c9a227 0%, #a88520 50%, #8b6914 100%)' }}>
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                      {Array.from({ length: 12 }).map((_, i) => (
                        <div key={i} className="absolute w-px h-px bg-white rounded-full animate-pulse" style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.3 + 0.15, animationDelay: `${Math.random() * 3}s` }} />
                      ))}
                    </div>
                    <div className="relative flex items-center gap-6">
                      <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0 group-hover:bg-white/25 transition-colors">
                        <Flower2 className="w-8 h-8 text-white" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-heading text-white mb-1 group-hover:text-white transition-colors">
                          Campus de Interioridad
                        </h3>
                        <p className="text-white/60 text-sm">
                          {campusAccess ? 'Entra a tu espacio sagrado' : 'Descubre tu espacio de consciencia'}
                        </p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                </Link>
              </section>

            {/* Special Course Enrollments */}
            {specialEnrollments.length > 0 && (
              <section className="mb-12">
                <h2 className="text-2xl font-heading text-[hsl(var(--text-primary))] mb-6">
                  Mis formaciones especiales
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {specialEnrollments.map((enrollment, index) => {
                    const courseAccessEnabled = enrollment.course?.course_access_enabled;
                    const canAccess = courseAccessEnabled && enrollment.status === 'active';
                    
                    return (
                      <div 
                        key={enrollment.id}
                        className="group"
                        data-testid={`special-enrollment-${index}`}
                      >
                        <div className="card-soft overflow-hidden">
                          <div className="h-40 rounded-2xl overflow-hidden mb-4 relative">
                            <img
                              src={enrollment.course?.hero_image_url || 'https://images.unsplash.com/photo-1516961642265-531546e84af2?fit=crop&w=800&q=80'}
                              alt={enrollment.course?.title}
                              className={`w-full h-full object-cover ${!canAccess ? 'opacity-70' : 'group-hover:scale-105'} transition-transform duration-500`}
                            />
                            {!canAccess && (
                              <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                <div className="bg-white/90 rounded-full p-3">
                                  <Lock className="w-6 h-6 text-[hsl(var(--text-secondary))]" />
                                </div>
                              </div>
                            )}
                          </div>
                          <h3 className="font-heading text-lg text-[hsl(var(--text-primary))] mb-2">
                            {enrollment.course?.title}
                          </h3>
                          
                          {/* Status badges */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            {enrollment.status === 'active' ? (
                              <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                <CheckCircle className="w-3 h-3" />
                                Inscripción activa
                              </span>
                            ) : (
                              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
                                {enrollment.status === 'pending_deposit' ? 'Pendiente señal' : 
                                 enrollment.status === 'pending_subscription' ? 'Pendiente pago' : enrollment.status}
                              </span>
                            )}
                            
                            {enrollment.status === 'active' && !courseAccessEnabled && (
                              <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                <Clock className="w-3 h-3" />
                                Próximamente
                              </span>
                            )}
                          </div>
                          
                          {canAccess ? (
                            <Link to={`/formaciones/${enrollment.course?.slug}/curso`}>
                              <Button className="w-full btn-gold">
                                Acceder al curso
                              </Button>
                            </Link>
                          ) : enrollment.status === 'active' ? (
                            <div className="text-center p-3 bg-[hsl(var(--sage-light))] rounded-xl">
                              <p className="text-sm text-[hsl(var(--text-secondary))]">
                                El curso comenzará pronto. Te avisaremos cuando esté disponible.
                              </p>
                            </div>
                          ) : (
                            <Link to={`/formaciones/${enrollment.course?.slug}/inscripcion`}>
                              <Button variant="outline" className="w-full">
                                Completar inscripción
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Recommended Courses */}
            {courses.filter(c => !enrollments.some(e => e.course_id === c.id)).length > 0 && (
              <section>
                <h2 className="text-2xl font-heading text-[hsl(var(--text-primary))] mb-6">
                  Formaciones recomendadas
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {courses
                    .filter(c => !enrollments.some(e => e.course_id === c.id))
                    .slice(0, 3)
                    .map((course, index) => (
                      <Link 
                        key={course.id}
                        to={`/formaciones/${course.id}`}
                        className="group"
                        data-testid={`recommended-course-${index}`}
                      >
                        <div className="card-soft overflow-hidden hover-lift">
                          <div className="h-40 rounded-2xl overflow-hidden mb-4">
                            <img
                              src={course.image_url || 'https://images.unsplash.com/photo-1516961642265-531546e84af2?fit=crop&w=800&q=80'}
                              alt={course.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="w-4 h-4 text-[hsl(var(--sage))]" strokeWidth={1.5} />
                            <span className="text-sm text-[hsl(var(--sage))]">{course.duration}</span>
                          </div>
                          <h3 className="font-heading text-lg text-[hsl(var(--text-primary))] mb-2 group-hover:text-[hsl(var(--accent))] transition-colors">
                            {course.title}
                          </h3>
                          <div className="flex items-center justify-between">
                            <span className="text-xl font-heading text-[hsl(var(--accent))]">
                              {course.price}€
                            </span>
                            <span className="text-sm text-[hsl(var(--sage))] flex items-center gap-1">
                              Ver más <ArrowRight className="w-4 h-4" />
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                </div>
              </section>
            )}
          </>
        )}

        {/* Inspiration Quote */}
        <div className="mt-12 card-soft text-center p-8 bg-gradient-to-br from-[hsl(var(--accent))]/5 to-white">
          <Sparkles className="w-8 h-8 text-[hsl(var(--accent))] mx-auto mb-4" strokeWidth={1.5} />
          <p className="font-accent text-xl italic text-[hsl(var(--text-primary))]">
            "Cada paso que das hacia ti mismo es un regreso a casa."
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
